import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import classes from "./LoginRegister.module.scss";
import trophia_logo from "../../assets/trophiaLogo.png";

const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:4000";

// Step 1: email only
// Step 2a "login"            — password field
// Step 2b "complete-profile" — dietitian-added client sets their password
// Step 2c "register"         — new user, full form
// Step 2d "forgot-password"  — send reset email
// Step 2e "reset-sent"       — confirmation

type Step = "email" | "login" | "complete-profile" | "register" | "forgot-password" | "reset-sent";

const Logo = () => (
  <div className={classes.logo}>
   <img src={trophia_logo} alt="Trophia logo" className={classes.logo_logo}/> 
  </div>
);

const EmailIcon = () => (
  <svg className={classes.input_icon} width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const LockIcon = () => (
  <svg className={classes.input_icon} width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const UserIcon = () => (
  <svg className={classes.input_icon} width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const getStrength = (pw: string): { score: number; label: string; color: string } => {
  if (!pw) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pw.length >= 6)                     score++;
  if (pw.length >= 10)                    score++;
  if (/[0-9]/.test(pw))                  score++;
  if (/[^A-Za-z0-9]/.test(pw))           score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  score = Math.min(score, 4);
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["", "#E05C5C", "#E09025", "#7DB55A", "#4A7C40"];
  return { score, label: labels[score], color: colors[score] };
};

const LoginRegister = () => {
  const { login, register, error: authError } = useAuth();
  const navigate = useNavigate();

  const [step,       setStep]       = useState<Step>("email");
  const [email,      setEmail]      = useState("");
  const [name,       setName]       = useState("");
  const [password,   setPassword]   = useState("");
  const [confirmPw,  setConfirmPw]  = useState("");
  const [checkErr,   setCheckErr]   = useState("");
  const [checking,   setChecking]   = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [resetErr,     setResetErr]     = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setResetErr("");
    setResetLoading(true);
    try {
      const res  = await fetch(`${BASE}/api/auth/forgot-password`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) { setResetErr(data.error ?? "Failed to send reset email."); return; }
      setStep("reset-sent");
    } catch {
      setResetErr("Could not reach the server. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  const strength = getStrength(password);
  const isStepWithPassword = step === "register" || step === "complete-profile";
  const passwordTooWeak    = isStepWithPassword && strength.score < 3;

  const StrengthBar = () => strength.score === 0 ? null : (
    <div className={classes.strength_wrap}>
      <div className={classes.strength_track}>
        {[1,2,3,4].map(n => (
          <div
            key={n}
            className={classes.strength_segment}
            style={{ background: n <= strength.score ? strength.color : undefined }}
          />
        ))}
      </div>
      <span className={classes.strength_label} style={{ color: strength.color }}>
        {strength.label}
      </span>
    </div>
  );

  // ── Step 1: check email ───────────────────────────────
  const handleCheckEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setCheckErr("");
    setChecking(true);
    try {
      const res  = await fetch(`${BASE}/api/auth/check-email`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) { setCheckErr(data.error ?? "Error checking email"); return; }
      if (data.status === "complete-profile") setName(data.name ?? "");
      setStep(data.status as Step);
    } catch {
      setCheckErr("Could not reach the server. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  // ── Step 2: submit ────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const loggedInUser = step === "login"
        ? await login(email, password)
        : await register(name.trim() || email.split("@")[0], email, password);
      navigate(loggedInUser.role === "dietitian" ? "/dietitian" : "/dashboard");
    } catch {
      // error shown via authError from context
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    setStep("email");
    setPassword("");
    setConfirmPw("");
    setCheckErr("");
  };

  // ── Titles per step ───────────────────────────────────
  const stepTitle: Record<Step, string> = {
    "email":            "Welcome back",
    "login":            "Enter your password",
    "complete-profile": "Complete your profile",
    "register":         "Create an account",
    "forgot-password":  "Reset your password",
    "reset-sent":       "Check your email",
  };
  const stepSub: Record<Step, string> = {
    "email":            "Enter your email to continue",
    "login":            email,
    "complete-profile": `Hi ${name || "there"}! Your dietitian has added you. Set a password to access your account.`,
    "register":         "No account found — register below",
    "forgot-password":  "We'll send a reset link to your email address.",
    "reset-sent":       `A password reset link has been sent to ${email}`,
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.card}>
        <Logo />

        <div className={classes.step_header}>
          <h2 className={classes.step_title}>{stepTitle[step]}</h2>
          <p className={classes.step_sub}>{stepSub[step]}</p>
        </div>

        {/* ── Step 1: Email ── */}
        {step === "email" && (
          <form className={classes.form} onSubmit={handleCheckEmail}>
            <div className={classes.input_group}>
              <EmailIcon />
              <input className={classes.input} type="email" placeholder="Email address"
                value={email} onChange={e => setEmail(e.target.value)} autoFocus required />
            </div>
            {checkErr && <p className={classes.auth_error}>{checkErr}</p>}
            <button type="submit" className={classes.submit_btn} disabled={checking || !email}>
              {checking ? <span className={classes.btn_spinner} /> : "Continue →"}
            </button>
          </form>
        )}

        {/* ── Step 2a: Login ── */}
        {step === "login" && (
          <form className={classes.form} onSubmit={handleSubmit}>
            <div className={classes.input_group}>
              <LockIcon />
              <input className={classes.input} type="password" placeholder="Password"
                value={password} onChange={e => setPassword(e.target.value)} autoFocus required />
              <button type="button" className={classes.forgot_btn}
                onClick={() => { setStep("forgot-password"); setPassword(""); }}>
                Forgot password?
              </button>
            </div>
            {authError && <p className={classes.auth_error}>{authError}</p>}
            <button type="submit" className={classes.submit_btn} disabled={submitting || !password}>
              {submitting ? <span className={classes.btn_spinner} /> : "Log in"}
            </button>
            <button type="button" className={classes.back_btn} onClick={goBack}>← Back</button>
          </form>
        )}

        {/* ── Step 2d: Forgot password ── */}
        {step === "forgot-password" && (
          <form className={classes.form} onSubmit={handleForgotPassword}>
            <div className={classes.input_group}>
              <EmailIcon />
              <input className={classes.input} type="email" placeholder="Email address"
                value={email} onChange={e => setEmail(e.target.value)} autoFocus required />
            </div>
            {resetErr && <p className={classes.auth_error}>{resetErr}</p>}
            <button type="submit" className={classes.submit_btn} disabled={resetLoading || !email}>
              {resetLoading ? <span className={classes.btn_spinner} /> : "Send Reset Link"}
            </button>
            <button type="button" className={classes.back_btn} onClick={goBack}>← Back</button>
          </form>
        )}

        {/* ── Step 2e: Reset email sent ── */}
        {step === "reset-sent" && (
          <div className={classes.reset_sent_wrap}>
            <div className={classes.reset_sent_icon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4A7C40" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
            </div>
            <p className={classes.reset_sent_msg}>
              Check your inbox and click the link to reset your password. The link expires in 1 hour.
            </p>
            <p className={classes.reset_sent_spam}>If you don't see it, check your spam folder.</p>
            <button type="button" className={classes.submit_btn} onClick={goBack}>
              Back to Login
            </button>
          </div>
        )}

        {/* ── Step 2b: Complete profile (dietitian-added client) ── */}
        {step === "complete-profile" && (
          <form className={classes.form} onSubmit={handleSubmit}>
            <div className={classes.input_group}>
              <LockIcon />
              <input className={classes.input} type="password" placeholder="Choose a password"
                value={password} onChange={e => setPassword(e.target.value)} autoFocus required />
            </div>
            <StrengthBar />
            {password && passwordTooWeak && (
              <p className={classes.strength_hint}>Min. 6 characters with a number and a special character (!@#$…)</p>
            )}
            <div className={classes.input_group}>
              <LockIcon />
              <input className={classes.input} type="password" placeholder="Confirm password"
                value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required />
            </div>
            {authError && <p className={classes.auth_error}>{authError}</p>}
            {confirmPw && password !== confirmPw && (
              <p className={classes.auth_error}>Passwords do not match</p>
            )}
            <button type="submit" className={classes.submit_btn}
              disabled={submitting || !password || password !== confirmPw || passwordTooWeak}>
              {submitting ? <span className={classes.btn_spinner} /> : "Set Password & Continue"}
            </button>
            <button type="button" className={classes.back_btn} onClick={goBack}>← Back</button>
          </form>
        )}

        {/* ── Step 2c: Register ── */}
        {step === "register" && (
          <form className={classes.form} onSubmit={handleSubmit}>
            <div className={classes.input_group}>
              <UserIcon />
              <input className={classes.input} type="text" placeholder="Full Name"
                value={name} onChange={e => setName(e.target.value)} autoFocus required />
            </div>
            <div className={classes.input_group}>
              <LockIcon />
              <input className={classes.input} type="password" placeholder="Password"
                value={password} onChange={e => setPassword(e.target.value)} required />
            </div>
            <StrengthBar />
            {password && passwordTooWeak && (
              <p className={classes.strength_hint}>Min. 6 characters with a number and a special character (!@#$…)</p>
            )}
            <div className={classes.input_group}>
              <LockIcon />
              <input className={classes.input} type="password" placeholder="Confirm Password"
                value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required />
            </div>
            {authError && <p className={classes.auth_error}>{authError}</p>}
            {confirmPw && password !== confirmPw && (
              <p className={classes.auth_error}>Passwords do not match</p>
            )}
            <button type="submit" className={classes.submit_btn}
              disabled={submitting || !name || !password || password !== confirmPw || passwordTooWeak}>
              {submitting ? <span className={classes.btn_spinner} /> : "Create Account"}
            </button>
            <button type="button" className={classes.back_btn} onClick={goBack}>← Back</button>
          </form>
        )}

        {/* Footer */}
        <footer className={classes.footer}>
          <p className={classes.footer_copy}>© {new Date().getFullYear()} Trophia · Tatiana Karam. All rights reserved.</p>
          <div className={classes.footer_icons}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="0.5" fill="#888" />
            </svg>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
            </svg>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LoginRegister;
